<template>
  <div class="enquire-now-container">
    <div class="enquire-now-header">
      <p class="text-center text-weight-bold" v-if="descriptionText" v-html="descriptionText"></p>
      <p class="text-center text-weight-bold" v-else v-html="subheaderText"></p>
    </div>
    <div class="enquire-now-body">
      <QForm ref="enquireNowForm" class="enquire-now-form" @submit.prevent="handleSubmit">
        <OptionGroupField
          v-model="form.enquireType"
          containerClass="customer-selection-section"
          :options="enquireOptions"
          :required="true"
          :inline="true"
          color="secondary"
          :disable="isFormSubmitting"
          @input="onEnquireTypeChange"
        >
          <template v-slot:label="{ opt, hasError }">
            <div class="customer-selection-option">
              <span
                class="customer-selection-option-label text-weight-bold"
                :style="hasError ? { color: 'var(--q-color-negative)', fontWeight: '500' } : {}"
              >
                {{ opt.label }}
              </span>
              <p v-html="opt.description"></p>
            </div>
          </template>
        </OptionGroupField>

        <QSeparator class="q-my-lg" />
        <div class="business-lookup-section">
          <div class="no-nzbn-section">
            <QCheckbox v-if="showNoNZBN" v-model="noNZBN" color="primary" label="No NZBN" left-label @input="onNoNZBNChange" />
          </div>

          <SearchCustomer v-if="!noNZBN" v-model="form.customer" :loading="isFormSubmitting" />
          <InputField
            v-else
            label="Company name"
            containerClass="customer-details-input-group col-12"
            :placeholder="handleLabel(companyName, 'Add company name')"
            v-model="companyName"
            type="name"
            :minLength="2"
            :maxLength="100"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
        </div>
        <div v-if="errorMsg && !hasBusinessCustomer" class="error-message text-red q-mt-sm q-mb-md">
          {{ errorMsg }}
        </div>
        <QSeparator class="q-my-lg" />
        <div class="customer-details-section row q-col-gutter-lg q-mb-lg">
          <p class="customer-details-label col-12 text-secondary">Your details</p>

          <InputField
            label="First name"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.firstName, 'Add first name')"
            v-model="form.contact.firstName"
            type="name"
            :minLength="2"
            :maxLength="40"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            label="Last name"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.lastName, 'Add last name')"
            v-model="form.contact.lastName"
            type="name"
            :minLength="2"
            :maxLength="40"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            label="Middle name"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.middleName, 'Add middle name')"
            v-model="form.contact.middleName"
            type="name"
            :minLength="2"
            :maxLength="40"
            :required="false"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <div class="w-100" />
          <InputField
            v-if="isNZ"
            label="Phone"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.phone, 'Add contact phone number')"
            v-model="form.contact.phone"
            type="tel"
            :minLength="9"
            :maxLength="12"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            v-else
            label="Phone"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.phone, 'Add contact phone number')"
            v-model="form.contact.phone"
            type="tel"
            :length="10"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            label="Email"
            containerClass="customer-details-input-group col-12 col-sm-6"
            :placeholder="handleLabel(form.contact.email, 'Add email')"
            v-model="form.contact.email"
            type="email"
            :required="true"
            :minLength="2"
            :maxLength="55"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            v-if="isNZ"
            label="Location"
            containerClass="customer-details-input-group col-12 col-sm-4"
            :placeholder="handleLabel(form.contact.location, 'Add location')"
            v-model="form.contact.location"
            :required="true"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            v-else
            label="Postcode"
            containerClass="customer-details-input-group col-12 col-sm-4"
            :placeholder="handleLabel(form.contact.postCode, 'Add postcode')"
            v-model="form.contact.postCode"
            :required="true"
            :length="4"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            v-model="form.fleetSize"
            containerClass="customer-details-input-group col-12 col-sm-4"
            label="Fleet size"
            :placeholder="handleLabel(form.fleetSize, 'Add fleet size')"
            :required="true"
            type="number"
            :maxLength="4"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <SelectField
            v-model="form.yearsTrading"
            containerClass="customer-details-input-group years-trading col-12 col-sm-4"
            label="Number of years trading"
            :placeholder="handleLabel(form.yearsTrading, 'Select')"
            :options="yearsTradingOptions"
            optionValue="value"
            optionLabel="label"
            required
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
          <InputField
            label="Leave a message"
            containerClass="customer-details-input-group col-12"
            type="textarea"
            :maxLength="250"
            :placeholder="handleLabel(form.message, 'Type your message...')"
            v-model="form.description"
            :disable="isFormSubmitting"
            :hasRequiredLabel="false"
          />
        </div>
        <div class="privacy-details-section">
          <p class="privacy-label text-weight-bold text-secondary">Privacy statement</p>
          <p class="privacy-description">
            By providing your details, you consent to us contacting you about our products and services (including comprehensive motor
            vehicle insurance). <a :href="privacyPolicyLink" target="_blank">FleetPartners Privacy Policy</a> contains information about how
            you can access and correct your personal information, how to make a complaint, and how we deal with complaints.
          </p>
        </div>
        <div class="submit-section">
          <CalculatorButton color="primary" rounded label="Submit" hasChevronIcon type="submit" :loading="isFormSubmitting" />
        </div>
      </QForm>
    </div>
  </div>
</template>

<script>
import { QForm, QSeparator, QCheckbox } from 'quasar';
import CalculatorButton from '../../components/Calculator/CalculatorButton';
import SearchCustomer from '../../components/Calculator/SearchCustomer/SearchCustomer';
import InputField from '../../components/Atoms/InputField';
import SelectField from '../../components/Atoms/SelectField';
import OptionGroupField from '../../components/Atoms/OptionGroupField.vue';
import { mapGetters, mapMutations } from 'vuex';
import { shortListOperationsMixin } from '../../mixins/calculator';
import { getSubdomain } from '../../utils/get-subdomain';
import { fetchSfAccountAndOppMeta } from '../../utils/fetchSfAccountAndOppMeta';
import { omitObjectKeys } from '../../utils/helpers';
import { evaCloseCircleOutline } from '@quasar/extras/eva-icons';
import { CalculatorDialog } from '../../components/Calculator/CalculatorDialog';
import MutateSaveCalculatorLeadDialler from '../../graphql/mutations/MutateSaveCalculatorLeadDialler';
import MutateSaveCalculatorOnlineAccount from '../../graphql/mutations/MutateSaveCalculatorOnlineAccount';
import MutateUpdateSalesforceLead from '../../graphql/mutations/MutateUpdateSalesforceLead';
import {
  GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
  GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
  GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CREATE_ACCOUNT,
  GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CALL_ME,
  GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CALL_ME_2,
  CALCULATOR_ENQUIRE_TYPE,
  ENQUIRE_SHORTLIST_PAYLOAD_OMIT_KEYS
} from '../../utils/constants';

export default {
  name: 'EnquireNowPage',
  props: {
    showCustomerSelectionSection: {
      type: Boolean,
      default: true
    },
    vehicle: {
      type: Object
    },
    descriptionText: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      sort: false,
      errorMsg: '',
      form: {
        yearsTrading: {},
        contact: {},
        enquireType: ''
      },
      enquireOptions: [
        {
          label: 'Call me',
          description:
            'One of our leasing specialists will contact you shortly about your vehicle shortlist and answer any questions you have all obligation-free.',
          value: CALCULATOR_ENQUIRE_TYPE.CALL_ME
        },
        {
          label: 'Create an online account',
          description:
            'Creating an account will save your vehicle shortlist for later, allow you to search for more vehicles, and if eligible let you securely and quickly complete our online  application for a credit limit, so you can then request a quote on the specific vehicle/s you need.',
          value: CALCULATOR_ENQUIRE_TYPE.CREATE_ACCOUNT
        }
      ],
      fleetSizeOptions: Array.from({ length: 3 }, (_, index) => ({ label: String(index + 1), value: index + 1 })),
      yearsTradingOptions: [],
      yearsTradingOptionsAU: [
        {
          label: 'Min 2 years trading',
          value: 'Minimum 2 years'
        },
        {
          label: '2 - 5 years',
          value: '2 - 5 years'
        },
        {
          label: '5 - 10 years',
          value: '5 - 10 years'
        },
        {
          label: 'More than 10 years',
          value: 'More than 10 years'
        }
      ],
      yearsTradingOptionsNZ: [
        {
          label: 'Min 1 year trading',
          value: 'Minimum 1 year'
        },
        {
          label: '1 - 5 years',
          value: '1 - 5 years'
        },
        {
          label: '5 - 10 years',
          value: '5 - 10 years'
        },
        {
          label: 'More than 10 years',
          value: 'More than 10 years'
        }
      ],
      noNZBN: false,
      companyName: '',
      isFormSubmitting: false
    };
  },
  mixins: [shortListOperationsMixin],
  computed: {
    ...mapGetters({
      isNZ: 'theme/isNZ',
      calculator: 'theme/getCalculator',
      orgProfile: 'theme/getOrgProfile'
    }),
    privacyPolicyLink() {
      return this.isNZ ? 'https://www.fleetpartners.co.nz/privacy-policy' : 'https://www.fleetpartners.com.au/privacy-policy';
    },
    subheaderText() {
      const defaultSubheader =
        'Interested to find out more about FleetPartners Operating Leases for your business? <br />Simply fill in the form below.';
      return this.calculator?.enquireNowSubheader_Disabled || defaultSubheader;
    },
    showNoNZBN() {
      return this.isNZ && this.form?.enquireType === CALCULATOR_ENQUIRE_TYPE.CALL_ME;
    },
    hasBusinessCustomer() {
      const c = this.form.customer;
      return !!(c && String(c.name || '').trim() && String(c.abn || '').trim());
    }
  },
  mounted() {
    if (!this.showCustomerSelectionSection && this.form) {
      this.form.enquireType = CALCULATOR_ENQUIRE_TYPE.CALL_ME;
    }
  },
  methods: {
    ...mapMutations({
      setMessage: 'theme/setMessage'
    }),
    handleLabel(value, label) {
      if (!value) return label;
    },
    onEnquireTypeChange() {
      this.noNZBN = false;
      this.companyName = '';
    },
    onNoNZBNChange(checked) {
      if (checked) {
        this.form.customer = null;
        this.errorMsg = '';
      } else {
        this.companyName = '';
      }
    },
    async handleSubmit() {
      const isValid = await this.$refs.enquireNowForm.validate();

      if (!isValid) {
        return;
      }

      if (!this.noNZBN && !this.hasBusinessCustomer) {
        this.errorMsg = 'Please search for your business and select it from the results.';
        return;
      }

      this.errorMsg = '';

      const {
        orgId,
        whiteLabel: { introducerName, nzbn, abn, region, marketingConsent }
      } = this.orgProfile;

      const shortlistsCombined = this.vehicle ? [{ ...this.vehicle, isShortlisted: true }] : this.shortlist;

      const { utm_source, utm_medium, utm_campaign, utm_content, utm_term } = this.$route.query;
      const truncateUtmParameter = (value) => (typeof value === 'string' ? value.slice(0, 254) : value);

      const payload = {
        ...this.form,
        customer: this.noNZBN ? { name: String(this.companyName || '').trim(), abn: null, noNZBN: 'Yes' } : this.form.customer,
        utmParameters: {
          utmSource: truncateUtmParameter(utm_source),
          utmMedium: truncateUtmParameter(utm_medium),
          utmCampaign: truncateUtmParameter(utm_campaign),
          utmContent: truncateUtmParameter(utm_content),
          utmTerm: truncateUtmParameter(utm_term),
          referringPageUrl: truncateUtmParameter(document.referrer || window.location.href)
        },
        yearsTrading: this.form.yearsTrading.value,
        shortlists: shortlistsCombined.map((row) => omitObjectKeys(row, ENQUIRE_SHORTLIST_PAYLOAD_OMIT_KEYS)),
        introducer: {
          region,
          abn: abn || nzbn,
          orgId,
          name: introducerName,
          domainName: getSubdomain(this.$route)
        }
      };

      let stage = GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CREATE_ACCOUNT;

      if (this.vehicle) stage = GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CALL_ME_2;
      else if (this.form.enquireType === CALCULATOR_ENQUIRE_TYPE.CALL_ME) stage = GTM_EVENT_STAGE_SUBMIT_ENQUIRY_CALL_ME;

      try {
        this.isFormSubmitting = true;

        setTimeout(() => {
          this.$gtm.trackEvent({
            event: GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
            stage,
            data: {
              ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
              ...(this.form?.contact?.firstName?.length && { firstName: this.form?.contact?.firstName }),
              ...(this.form?.contact?.lastName?.length && { lastName: this.form?.contact?.lastName }),
              ...(this.form?.contact?.middleName?.length && { middleName: this.form?.contact?.middleName }),
              ...((this.form?.customer?.name || this.companyName)?.length && {
                companyName: this.form?.customer?.name || this.companyName
              }),
              ...(this.form?.contact?.email?.length && { email: this.form?.contact?.email }),
              ...(this.form?.contact?.phone?.length && { phoneNumber: this.form?.contact?.phone }),
              ...(this.form?.yearsTrading?.value?.length && { numberOfYearsTrading: this.form?.yearsTrading?.value }),
              ...(this.form?.fleetSize?.length && { fleetSize: this.form?.fleetSize })
            }
          });
        });

        if (this.form.enquireType === CALCULATOR_ENQUIRE_TYPE.CALL_ME) {
          const diallerResponse = await this.$apollo.mutate({
            mutation: MutateSaveCalculatorLeadDialler,
            client: 'boomiClient',
            variables: {
              calcLeadDialler: {
                ...payload
              }
            }
          });

          const leadId = diallerResponse?.data?.saveCalculatorLeadDialler?.leadId;

          if (leadId && marketingConsent) {
            this.$apollo
              .mutate({
                mutation: MutateUpdateSalesforceLead,
                variables: {
                  leadId,
                  payload: {
                    marketingConsent
                  }
                }
              })
              .catch((consentError) => {
                // eslint-disable-next-line no-console
                console.log({ consentError });
              });
          }

          this.$router.push({ name: 'calculatorRegistrationSuccess' });
        } else {
          const salesforce = await fetchSfAccountAndOppMeta(this.$apollo, {
            customer: this.form.customer,
            introducerName,
            region,
            isNZ: this.isNZ
          });

          let response = await this.$apollo.mutate({
            mutation: MutateSaveCalculatorOnlineAccount,
            client: 'boomiClient',
            variables: {
              calcLeadOpp: {
                ...payload,
                salesforce
              }
            }
          });

          const { message, status } = response?.data?.saveCalculatorOnlineAccount;

          if (status == 'error') {
            this.$euiNotifications.emit({
              notification: CalculatorDialog,
              headerIcon: evaCloseCircleOutline,
              header: message,
              headerStyle: 'font-size: 24px; line-height: 1.5'
            });
          } else if (status == 'success' && message != '') {
            this.setMessage(message);
            this.$router.push({ name: 'calculatorRegistrationSuccess', query: { redirect: true } });
          } else if (status == 'success' && this.form.enquireType === CALCULATOR_ENQUIRE_TYPE.CREATE_ACCOUNT) {
            this.$router.push({ name: 'calculatorRegistrationSuccess', query: { onlineAccount: true } });
          } else {
            this.$router.push({ name: 'calculatorRegistrationSuccess' });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      } finally {
        this.isFormSubmitting = false;
      }
    }
  },
  created() {
    this.yearsTradingOptions = this.isNZ ? this.yearsTradingOptionsNZ : this.yearsTradingOptionsAU;
    this.form.yearsTrading = this.yearsTradingOptions[0];

    /*
    TODO: Temporary disabling SCOR-3220
    this.enquireOptions = this.enquireOptions.map((option) => {
      if (option.value === 'call' && this.calculator?.callMeContent) {
        return { ...option, description: this.calculator.callMeContent };
      }

      if (option.value === 'register' && this.calculator?.onlineAccountContent) {
        return { ...option, description: this.calculator.onlineAccountContent };
      }

      return option;
    });
  */
  },
  components: {
    QForm,
    QSeparator,
    QCheckbox,
    InputField,
    SelectField,
    OptionGroupField,
    CalculatorButton,
    SearchCustomer
  }
};
</script>

<style lang="sass">
.enquire-now-container
  max-width: 650px
  width: 100%
  display: flex
  flex-flow: column wrap
  align-items: center

  .no-nzbn-section
    display: flex
    flex-flow: row wrap
    align-items: center
    justify-content: flex-start
    margin-bottom: 10px
    color: var(--q-color-primary)
    font-weight: 700

    .q-checkbox
      margin-right: 10px

  .enquire-now-header
    display: flex
    flex-flow: column wrap
    align-items: center

  .enquire-now-body
    display: flex
    flex-flow: column wrap
    align-items: center

    .enquire-now-form
      width: 100%
      .customer-selection-section
        .q-option-group
          &.q-option-group--inline
            display: flex
            flex-flow: row wrap
            justify-content: space-between
            &> div
              width: calc(50% - 10px)
              .q-radio__label .customer-selection-option
                line-height: 17px
                font-size: 13px
                .customer-selection-option-label
                  font-weight: normal
                  display: block
                  font-size: 16px
                  margin-bottom: 10px


          .q-radio
            align-items: flex-start!important

      .business-lookup-section
        .customer-details-input-group
          .field-label
            font-size: 16px
            font-weight: 700
      .customer-details-section
        .customer-details-label
          font-size: 18px
          font-weight: 700
          margin-bottom: -5px
        .customer-details-input-group
          .field-label
            font-size: 16px
            font-weight: 700
          .field-select
            padding-top: 10px
          &.years-trading
            .q-field__append
              padding-left: 0
      .privacy-details-section .privacy-label
        font-size: 18px
      .submit-section
        margin-top: 25px
        display: flex
        flex-flow: column wrap
        align-items: center
        .error-message
          width: 100%; font-size: 12px

        .calculator-button.q-btn
          width: 200px
          height: auto

  @media (max-width: 599px)
    .enquire-now-header
      max-width: 378px
    .enquire-now-body .enquire-now-form
      .customer-selection-section
        width: 100%
        .q-option-group.q-option-group--inline > div
          width: 100%
      .customer-details-section
        width: 100%
        margin: 0
        .customer-details-label
          padding: 0 0 20px 0
        .customer-details-input-group
          padding: 10px 0
      .business-lookup-section
        .abn-input-section
          margin-right: 0
          width: 100%
        .search-section
          margin-top: 10px
          width: 100%!important
          .q-btn.calculator-button
            width: 100%
      .submit-section .calculator-button.q-btn
        width: 100%
</style>
